/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ArrowRight,
  Banknote,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";

const PayoutDashboard = () => {
  const navigate = useNavigate();
  const { fetchPayoutSummary } = useContext(AdminContext);

  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      setIsLoading(true);
      try {
        const result = await fetchPayoutSummary();
        console.log("Payout Summary Response:", result);
        if (result?.success && result?.data) {
          const backendData = result.data;
          // Use all-time data for the main stats
          const allTime = backendData.allTime || {};
          const currentMonth = backendData.currentMonth || {};

          const mappedSummary = {
            // All-time totals - ensure proper number conversion from Decimal
            totalRevenue:
              Number(allTime.grossAmountETB || 0),
            totalDisbursed: Number(allTime.totalPaidToCaregiversETB || 0),
            pendingAmount: Number(backendData.pending?.totalAmountETB || 0),
            failedAmount: 0,
            caregiverShare: Number(allTime.totalPaidToCaregiversETB || 0),
            platformShare: Number(allTime.platformRevenueETB || 0),
            pendingCount: backendData.pending?.count || 0,
            failedCount: backendData.failed?.count || 0,
            // Current month for reference
            currentMonthRevenue:
              Number(currentMonth.grossAmountETB || 0),
            currentMonthCaregiver: Number(
              currentMonth.totalPaidToCaregiversETB || 0,
            ),
            currentMonthPlatform: Number(currentMonth.platformRevenueETB || 0),
            // Monthly breakdown for chart
            monthlyBreakdown: backendData.monthlyBreakdown || {},
          };
          console.log("Mapped Summary:", mappedSummary);
          setSummary(mappedSummary);
        } else {
          setSummary({
            totalRevenue: 0,
            totalDisbursed: 0,
            pendingAmount: 0,
            failedAmount: 0,
            caregiverShare: 0,
            platformShare: 0,
            pendingCount: 0,
            failedCount: 0,
            currentMonthRevenue: 0,
            currentMonthCaregiver: 0,
            currentMonthPlatform: 0,
            monthlyBreakdown: {},
          });
        }
      } catch (error) {
        console.error("Load summary error:", error);
        setSummary({
          totalRevenue: 0,
          totalDisbursed: 0,
          pendingAmount: 0,
          failedAmount: 0,
          caregiverShare: 0,
          platformShare: 0,
          pendingCount: 0,
          failedCount: 0,
          currentMonthRevenue: 0,
          currentMonthCaregiver: 0,
          currentMonthPlatform: 0,
          monthlyBreakdown: {},
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadSummary();
  }, [fetchPayoutSummary]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stats = [
    {
      label: "Total Revenue",
      value: `Br ${(summary?.totalRevenue || 0).toLocaleString()}`,
      icon: Banknote,
      change: 0,
      isPositive: true,
    },
    {
      label: "Total Disbursed",
      value: `Br ${(summary?.totalDisbursed || 0).toLocaleString()}`,
      icon: CheckCircle,
      change: 0,
      isPositive: true,
    },
    {
      label: "Pending Payouts",
      value: `Br ${(summary?.pendingAmount || 0).toLocaleString()}`,
      count: summary?.pendingCount || 0,
      icon: Clock,
      change: 0,
      isPositive: true,
    },
    {
      label: "Failed Payouts",
      value: summary?.failedCount || 0,
      count: summary?.failedCount || 0,
      icon: XCircle,
      change: 0,
      isPositive: false,
    },
  ];

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text dark:text-dark-text">
          Payout Dashboard
        </h1>
        <p className="text-gray-500 dark:text-dark-muted mt-1">
          Monitor platform revenue and caregiver payouts
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow"
          >
            {/* Large faded icon in background */}
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-white">
              <stat.icon size={64} />
            </div>

            {/* Content */}
            <div className="relative z-10">
              {/* Small icon with background */}
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3 bg-white/10 text-white">
                <stat.icon size={20} />
              </div>

              {/* Label */}
              <p className="text-white/70 font-medium text-sm">{stat.label}</p>

              {/* Value */}
              <h3 className="text-3xl font-bold text-white mt-1 mb-3">
                {stat.value}
              </h3>

              {/* Count badge for pending/failed */}
              {stat.count !== undefined && stat.count > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/60">
                    {stat.count} {stat.count === 1 ? "payout" : "payouts"}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Split Card */}
      <div className="bg-slate-900 rounded-xl shadow-sm border border-slate-800 p-6 mb-8">
        <h2 className="text-xl font-bold text-white mb-6">
          Revenue Split (80/20)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-emerald-900/20 rounded-lg border border-emerald-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-400">
                Caregiver Share (80% of net)
              </span>
              <TrendingUp className="text-emerald-400" size={20} />
            </div>
            <p className="text-2xl font-bold text-emerald-300">
              Br {(summary?.caregiverShare || 0).toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-blue-900/20 rounded-lg border border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-400">
                Platform Share (20% of net)
              </span>
              <p>ETB</p>
            </div>
            <p className="text-2xl font-bold text-blue-300">
              Br {(summary?.platformShare || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      <div className="bg-slate-900 rounded-xl shadow-sm border border-slate-800 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            Monthly Revenue Trend (Last 6 Months)
          </h2>
        </div>
        <div className="h-64">
          {summary?.monthlyBreakdown &&
          Object.keys(summary.monthlyBreakdown).length > 0 ? (
            <div className="h-full flex items-end justify-around gap-4 px-4">
              {Object.entries(summary.monthlyBreakdown)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([month, data]) => {
                  const maxValue = Math.max(
                    ...Object.values(summary.monthlyBreakdown).map(
                      (d) => d.grossAmount,
                    ),
                  );
                  const heightPercent =
                    maxValue > 0 ? (data.grossAmount / maxValue) * 100 : 0;
                  const monthName = new Date(month + "-01").toLocaleDateString(
                    "en-US",
                    { month: "short", year: "numeric" },
                  );

                  return (
                    <div
                      key={month}
                      className="flex-1 flex flex-col items-center gap-2"
                    >
                      <div className="w-full flex flex-col items-center justify-end h-48">
                        <div className="relative group w-full max-w-[60px]">
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            <div className="bg-gray-800 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg border border-gray-700">
                              <div className="font-bold mb-1">{monthName}</div>
                              <div className="text-emerald-400">
                                Caregiver: Br{" "}
                                {data.caregiverAmount.toLocaleString()}
                              </div>
                              <div className="text-blue-400">
                                Platform: Br {data.platformFee.toLocaleString()}
                              </div>
                              <div className="text-white font-bold mt-1">
                                Total: Br {data.grossAmount.toLocaleString()}
                              </div>
                            </div>
                          </div>
                          {/* Bar */}
                          <div
                            className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-lg transition-all duration-300 hover:from-emerald-500 hover:to-emerald-300 cursor-pointer"
                            style={{
                              height: `${heightPercent}%`,
                              minHeight: heightPercent > 0 ? "20px" : "0",
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-xs text-white/60 font-medium text-center">
                        {monthName}
                      </span>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-900/50 rounded-lg">
              <p className="text-gray-400">No payout data available yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => navigate("/payouts/list")}
          className="p-6 bg-slate-900 rounded-xl shadow-sm border border-slate-800 hover:shadow-md transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-bold text-white mb-1">
                View All Payouts
              </h3>
              <p className="text-sm text-gray-400">
                Manage and track all caregiver payouts
              </p>
            </div>
            <ArrowRight
              className="text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all"
              size={24}
            />
          </div>
        </button>

        <button
          onClick={() => navigate("/contracts")}
          className="p-6 bg-slate-900 rounded-xl shadow-sm border border-slate-800 hover:shadow-md transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-bold text-white mb-1">
                View Contracts
              </h3>
              <p className="text-sm text-gray-400">
                Manage active and terminated contracts
              </p>
            </div>
            <ArrowRight
              className="text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all"
              size={24}
            />
          </div>
        </button>
      </div>
    </div>
  );
};

export default PayoutDashboard;
